import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Activity, RefreshCw, AlertTriangle, MousePointerClick, Clock, MapPin } from "lucide-react";

/**
 * Rolls up FOYR_SESSIONS KV into a per-session card so we can answer
 * "how long was X on the site / what did they try / any errors".
 *
 * Companion to the Sign-ins table in AdminUsers — one row in Sign-ins
 * corresponds to N session cards here (one per browser tab they opened).
 */

interface EventRow {
  ts: string;
  name: string;
  route: string;
  payload: Record<string, unknown> | null;
}

interface ErrorRow {
  ts: string;
  kind: string;
  message: string;
  route: string;
  target?: string;
}

interface SessionRow {
  sessionId: string;
  email: string;
  startedAt: string;
  lastAt: string;
  durationMs: number;
  route: string;
  beats: number;
  routesVisited: string[];
  ua: string;
  tz: string;
  ip: string;
  country: string;
  events: EventRow[];
  errors: ErrorRow[];
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  if (m < 60) return `${m}m ${rs}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

export function SessionsPanel({ emailFilter }: { emailFilter?: string | undefined }): JSX.Element {
  const [rows, setRows] = useState<SessionRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function refresh(): Promise<void> {
    setBusy(true);
    setErr(null);
    try {
      const params = new URLSearchParams(window.location.search);
      const token = params.get("token") ?? "";
      const q = new URLSearchParams();
      if (token) q.set("token", token);
      if (emailFilter) q.set("email", emailFilter);
      const r = await fetch(`/api/sessions${q.toString() ? `?${q.toString()}` : ""}`, {
        credentials: "same-origin",
      });
      const text = await r.text();
      if (!r.ok) {
        if (r.status === 401) {
          throw new Error("Admin session not detected. Sign in as admin or pass ?token=…");
        }
        throw new Error(`api/sessions returned ${r.status}: ${text.slice(0, 140)}`);
      }
      const data = JSON.parse(text) as { sessions?: SessionRow[]; note?: string };
      setRows(data.sessions ?? []);
      if (data.note) setErr(data.note);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => { refresh(); }, [emailFilter]);

  const total = rows?.length ?? 0;
  const byEmail = useMemo(() => {
    const m = new Map<string, SessionRow[]>();
    for (const r of rows ?? []) {
      const arr = m.get(r.email) ?? [];
      arr.push(r);
      m.set(r.email, arr);
    }
    return m;
  }, [rows]);

  return (
    <div className="mt-6">
      <header className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-foyr" />
          <h2 className="display-3 text-ink-950">Sessions</h2>
          {rows ? (
            <span className="pill bg-cream-dark border border-border text-ink-700 text-[10px] tabular">
              {total} session{total === 1 ? "" : "s"} · {byEmail.size} user{byEmail.size === 1 ? "" : "s"}
            </span>
          ) : null}
        </div>
        <button onClick={refresh} disabled={busy} className="btn btn-outline !text-xs !py-1.5">
          <RefreshCw className={busy ? "w-3 h-3 animate-spin" : "w-3 h-3"} /> Refresh
        </button>
      </header>

      {err ? (
        <div className="panel p-3 border border-foyr/30 bg-foyr/5 text-[12.5px] text-foyr-deep mb-3">
          {err}
          {err.includes("KV not bound") ? (
            <div className="text-[11px] text-ink-500 mt-1">
              Operator: run <code className="font-mono">wrangler kv namespace create FOYR_SESSIONS</code>{" "}
              and paste the id into <code className="font-mono">wrangler.toml</code>, then redeploy.
            </div>
          ) : null}
        </div>
      ) : null}

      {rows && rows.length === 0 ? (
        <div className="panel p-4 text-[12.5px] text-ink-500 italic">
          No sessions captured yet. Heartbeats start the moment a user signs in and the page is visible.
        </div>
      ) : null}

      <div className="space-y-2">
        {(rows ?? []).map((r, i) => (
          <motion.div
            key={r.sessionId}
            initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
            className="panel p-3"
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[13px] font-medium text-ink-950 font-mono">{r.email}</div>
                <div className="text-[11px] text-ink-500 tabular mt-0.5 flex flex-wrap items-center gap-2">
                  <span><Clock className="w-3 h-3 inline mr-1" />{formatDuration(r.durationMs)}</span>
                  <span>· {new Date(r.startedAt).toLocaleString()}</span>
                  {r.country ? <span>· <MapPin className="w-3 h-3 inline" /> {r.country}</span> : null}
                  <span className="font-mono text-[10px] opacity-60">{r.sessionId.slice(0, 8)}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {r.errors.length > 0 ? (
                  <span className="pill bg-foyr/10 text-foyr-deep text-[10px] border border-foyr/30 inline-flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> {r.errors.length} error{r.errors.length === 1 ? "" : "s"}
                  </span>
                ) : null}
                {r.events.some((e) => e.name === "rage_click") ? (
                  <span className="pill bg-foyr text-cream text-[10px] inline-flex items-center gap-1">
                    <MousePointerClick className="w-3 h-3" /> rage-click
                  </span>
                ) : null}
                <span className="pill bg-cream-dark border border-border text-ink-500 text-[10px]">
                  {r.beats} beats · {r.routesVisited.length} routes
                </span>
              </div>
            </div>

            {r.routesVisited.length > 0 ? (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {r.routesVisited.slice(0, 12).map((route) => (
                  <span
                    key={route}
                    className="pill bg-cream-dark border border-border text-ink-700 text-[10px] font-mono"
                  >
                    {route}
                  </span>
                ))}
              </div>
            ) : null}

            {r.events.length > 0 ? (
              <details className="mt-2">
                <summary className="text-[11px] text-ink-500 cursor-pointer hover:text-ink-950">
                  {r.events.length} event{r.events.length === 1 ? "" : "s"}
                </summary>
                <ul className="mt-1 space-y-0.5 text-[11px] font-mono">
                  {r.events.slice(0, 50).map((e, j) => (
                    <li key={j} className="text-ink-700 tabular">
                      <span className="text-ink-500">{new Date(e.ts).toLocaleTimeString()}</span>{" "}
                      <span className="text-foyr">{e.name}</span>{" "}
                      <span className="text-ink-500">{e.route}</span>
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}

            {r.errors.length > 0 ? (
              <details className="mt-2" open>
                <summary className="text-[11px] text-foyr-deep cursor-pointer hover:text-ink-950">
                  errors / frustrations
                </summary>
                <ul className="mt-1 space-y-1 text-[11px]">
                  {r.errors.map((e, j) => (
                    <li key={j} className="border-l-2 border-foyr/40 pl-2 text-ink-700">
                      <span className="font-mono text-foyr">{e.kind}</span> on{" "}
                      <span className="font-mono">{e.route}</span>
                      <div className="text-ink-500 mt-0.5">{e.message}</div>
                      {e.target ? <div className="text-ink-500 mt-0.5 font-mono">{e.target}</div> : null}
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}

            <div className="mt-2 text-[10px] text-ink-500 truncate" title={r.ua}>
              {r.ua}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
